/* eslint-disable @typescript-eslint/naming-convention */
import { computed, observable } from 'mobx';
import TranslationRepository from '../../repositories/TranslationRepository';
import { formatPhone } from '../../utils/AddressFormUtils';
import Transaction from '../../domain/entities/Transaction/Transaction';
import Config from '../../domain/entities/Config/Config';
import CompanyBillingDetails from '../../domain/entities/CompanyBillingDetails';
import formatPrice from '../../utils/PriceFormatUtils';
import ConfigRepository from '../../repositories/ConfigRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import Address from '../../domain/entities/Address';
import { isTurkey } from '../../utils/CountryUtils';
import CartItem from '../../domain/entities/Cart/CartItem';
import { PAYMENT_TYPE } from '../../constants/PaymentProviders';
import { FIELD_TYPE } from '../../constants/Constants';

export const mapPaymentTypesToDisplayLabel: Record<PAYMENT_TYPE, string> = {
  [PAYMENT_TYPE.CC]: 'creditDebitCardOption',
  [PAYMENT_TYPE.PP]: 'creditDebitCardOption',
  [PAYMENT_TYPE.COD]: 'codPaymentOption',
};

export interface IOrderDataConfig {
  transactionRepository: TransactionRepository;
  configRepository: ConfigRepository;
  translationRepository: TranslationRepository;
}

interface ITranslations {
  creditDebitCardOption: string;
  codPaymentOption: string;
  [key: string]: string;
}

export default class OrderDataViewController {
  transactionRepository: TransactionRepository;

  configRepository: ConfigRepository;

  @observable transaction: Transaction;

  @observable translations: Partial<ITranslations> = {};

  private translationRepository: TranslationRepository;

  constructor(config: IOrderDataConfig) {
    this.transactionRepository = config.transactionRepository;
    this.configRepository = config.configRepository;
    this.translationRepository = config.translationRepository;
  }

  async loaded(): Promise<void> {
    this.transaction = await this.transactionRepository.getTransaction();
    this.translations = this.translationRepository?.
      listRequiredTranslationSchema(['creditDebitCardOption', 'codPaymentOption']) as ITranslations;
  }

  @computed get config(): Config {
    const { config } = this.configRepository;
    return config;
  }

  @computed get currency(): string {
    return this.config && this.config.currency;
  }

  @computed get userEmail(): string {
    return this.transaction && this.transaction.user.email;
  }

  @computed
  get shippingAddress(): Address {
    const shippingAddress = this.transaction && this.transaction.shippingAddress;

    return this.enhanceAddress(shippingAddress);
  }

  @computed
  get billingAddress(): Address {
    const billingAddress = this.transaction && this.transaction.billingAddress;

    return this.enhanceAddress(billingAddress);
  }

  @computed
  get companyBillingDetails(): CompanyBillingDetails {
    const companyBillingDetails = this.transaction && this.transaction.companyBillingDetails;

    return companyBillingDetails;
  }

  @computed
  get shippingFullName(): string {
    const {
      attributes: {
        first_name,
        last_name,
      },
    } = this.shippingAddress;

    return `${first_name} ${last_name}`;
  }

  @computed
  get shippingLineAddress(): string {
    return this.getLineAddress(this.shippingAddress);
  }

  @computed
  get billingLineAddress(): string {
    return this.getLineAddress(this.billingAddress);
  }

  getLineAddress(address: Address): string {
    const {
      attributes: {
        [FIELD_TYPE.FIRST_LINE_ADDRESS]: firstLineAddress,
        [FIELD_TYPE.SECOND_LINE_ADDRESS]: secondLineAddress,
        [FIELD_TYPE.THIRD_LINE_ADDRESS]: thirdLineAddress,
        [FIELD_TYPE.FIFTH_LINE_ADDRESS]: fifthLineAddress,
        [FIELD_TYPE.CITY]: city,
        [FIELD_TYPE.COUNTRY_ID]: countryId,
        [FIELD_TYPE.STATE]: state,
        [FIELD_TYPE.ZIPCODE]: zipcode,
      },
    } = address;

    if (!isTurkey(countryId)) {
      return `${firstLineAddress}, ${secondLineAddress}, 
        ${city}, ${(state && state !== 'undefined') ? `(${state}), ` : ''}
        ${zipcode}`;
    }
    return `${firstLineAddress},${secondLineAddress},
        ${zipcode}, ${city}, ${thirdLineAddress}, ${fifthLineAddress}`;
  }

  @computed get shippingCost(): string {
    const orderSummary = this.transaction && this.transaction.orderSummary;

    return orderSummary
      && formatPrice(this.config, orderSummary.totalShippingPrice);
  }

  @computed get totalCost(): string {
    const orderSummary = this.transaction && this.transaction.orderSummary;

    return orderSummary
      && formatPrice(this.config, orderSummary.totalCost);
  }

  @computed get sellPrice(): string {
    const orderSummary = this.transaction && this.transaction.orderSummary;

    return orderSummary
      && formatPrice(this.config, orderSummary.subTotal);
  }

  @computed get shippingPhone(): string {
    const {
      attributes: {
        [FIELD_TYPE.PHONE]: mobileNumber,
        [FIELD_TYPE.SECOND_PHONE]: secondPhoneNumber,
        [FIELD_TYPE.COUNTRY_ID]: countryId,
      },
    } = this.shippingAddress;

    const phoneNumberFormatted = formatPhone(
      mobileNumber.length ? mobileNumber : secondPhoneNumber, countryId,
    );
    return phoneNumberFormatted;
  }

  @computed
  public get cartItems(): CartItem[] {
    return (this.transaction && this.transaction.cart.cartItems) || [];
  }

  @computed
  public get paymentTypeLabel(): string {
    let paymentLabel = '';

    if (this.transaction && this.transaction.payment) {
      const { selectedPayment } = this.transaction.payment;
      const trPayment = this.transaction.getTransactionPaymentByProviderName(selectedPayment);
      paymentLabel = mapPaymentTypesToDisplayLabel[trPayment.paymentType as PAYMENT_TYPE];
    }

    return this.translations[paymentLabel] || '';
  }

  private enhanceAddress(address?: Address): Address {
    const enhancedAddress = address || new Address();
    const {
      [FIELD_TYPE.PHONE]: mobileNumber,
      [FIELD_TYPE.COUNTRY_ID]: countryId,
    } = enhancedAddress.attributes;

    if (!mobileNumber.startsWith('+')) {
      return Address.fromJSON({
        ...enhancedAddress.attributes,
        [FIELD_TYPE.PHONE]: formatPhone(mobileNumber, countryId),
      });
    }

    return address;
  }
}
